import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import type { PatientPhotoMedications } from "@/lib/api";
import { colors, fonts, radius } from "@/lib/theme";

/**
 * "N of the patient's photos show medications — Import from patient photos."
 *
 * The patient sent pictures through the pre-visit questionnaire; the ones the
 * triage read as a label or a pill organiser were run through the extraction
 * pipeline before the visit. Nothing lands in the nurse's review until she
 * taps Import, so a patient's blurry shelf never masquerades as her own run.
 *
 * Hidden when there is nothing to import and nothing still being read.
 */
export function PatientPhotoImportBanner({
    data,
    importing,
    onImport
}: {
    data: PatientPhotoMedications | null;
    importing: boolean;
    onImport: () => void;
}) {
    if (!data) {
        return null;
    }
    const reading = data.readingCount + data.pendingPhotoCount;
    const importable = data.importableDraftCount;
    if (importable === 0 && reading === 0) {
        return null;
    }
    const photoWord = data.photoCount === 1 ? "photo shows" : "photos show";
    const title =
        data.photoCount > 0
            ? `${data.photoCount} of the patient's ${photoWord} medications`
            : "The patient sent photos";
    const subtitle =
        importable > 0
            ? `${importable} medication${importable === 1 ? "" : "s"} read from ${
                  data.photoCount === 1 ? "it" : "them"
              } — review before saving.`
            : "Still reading them…";

    return (
        <View style={styles.banner} testID="patient-photo-import-banner">
            <View style={styles.icon}>
                <MaterialCommunityIcons
                    name="image-multiple-outline"
                    size={20}
                    color={colors.teal600}
                />
            </View>
            <View style={styles.textWrap}>
                <Text style={styles.title}>{title}</Text>
                <Text style={styles.subtitle}>{subtitle}</Text>
            </View>
            <Pressable
                onPress={onImport}
                disabled={importing || importable === 0}
                style={[styles.button, (importing || importable === 0) && styles.buttonDisabled]}
                accessibilityRole="button"
                accessibilityLabel="Import from patient photos"
                testID="patient-photo-import-button"
            >
                {importing || (importable === 0 && reading > 0) ? (
                    <ActivityIndicator size="small" color={colors.surface} />
                ) : (
                    <Text style={styles.buttonText}>Import</Text>
                )}
            </Pressable>
        </View>
    );
}

const styles = StyleSheet.create({
    banner: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        padding: 12,
        borderRadius: radius.lg,
        borderWidth: 1,
        borderColor: colors.teal100,
        backgroundColor: colors.teal50,
        marginHorizontal: 20,
        marginTop: 12
    },
    icon: {
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: colors.surface,
        alignItems: "center",
        justifyContent: "center"
    },
    textWrap: { flex: 1, gap: 2 },
    title: { fontSize: 14, fontFamily: fonts.bold, color: colors.textPrimary },
    subtitle: { fontSize: 12, fontFamily: fonts.regular, color: colors.textSecondary },
    button: {
        backgroundColor: colors.ink,
        borderRadius: radius.md,
        paddingVertical: 10,
        paddingHorizontal: 14,
        minWidth: 72,
        alignItems: "center"
    },
    buttonDisabled: { opacity: 0.5 },
    buttonText: { fontSize: 13, fontFamily: fonts.bold, color: colors.surface }
});
